import React, { useEffect, useMemo, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import withLoader from "../../components/layouts/WithLoader";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";
import SellingService from "../../services/SellingService";
import {
  exportToExcel,
  formatDate,
  formatPeriodDate,
  formatQty,
  noteToText,
  periodLabel,
  printConsolidation,
} from "../../utils/deliveryExport";

/*
 * Delivery follow-up. A card per day of the chosen period shows how many
 * purchase orders (BDC) are to be delivered and how many products; a "En retard"
 * card gathers the past delivery dates still open. Only BDC not finished
 * (En attente / En cours) are counted unless asked otherwise. Selecting a card
 * filters the list; "Produits à livrer" consolidates the products of the
 * selection (same backend result for screen, Excel and print).
 */
const PAGE_SIZE = 25;
const MAX_DAY_CARDS = 31;
const LATE_FROM = "2000-01-01";
const cardStyle = { borderRadius: 8, boxShadow: "0 1px 6px rgba(0,0,0,.08)" };
const labelStyle = {
  fontSize: "0.8rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "#6c757d",
};
const STATE_LIVRAISON = {
  0: { label: "En attente", cls: "badge-warning" },
  1: { label: "En cours", cls: "badge-primary" },
  2: { label: "Terminé", cls: "badge-success" },
  3: { label: "Livré", cls: "badge-success" },
};

const toIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const addDays = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  return toIso(new Date(y, m - 1, d + n));
};
const defaultRange = () => {
  const today = toIso(new Date());
  return { from: today, to: addDays(today, 14) };
};
// delivery dates are stored at UTC midnight
const dayKey = (value) => (value ? new Date(value).toISOString().slice(0, 10) : "");
const dayLabel = (iso) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    timeZone: "UTC",
  });

const emptyLoad = { bdcs: [], details: [] };

const SuiviLivraisons = ({ startLoading, stopLoading }) => {
  const initial = defaultRange();
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [includeFinished, setIncludeFinished] = useState(false);
  const [period, setPeriod] = useState(null); // { dateFrom, dateTo, bdcs, details }
  const [late, setLate] = useState(emptyLoad);
  const [horizon, setHorizon] = useState(emptyLoad); // next 30 days, independent of the chosen period
  const [selected, setSelected] = useState("all"); // "all" | "late" | yyyy-MM-dd
  const [page, setPage] = useState(0);
  const [products, setProducts] = useState(null); // consolidated result of the selection
  const [alert, setAlert] = useState({ open: false, text: "" });

  const showError = (text) => setAlert({ open: true, text });
  const today = toIso(new Date());

  const fetchRange = (from, to, withFinished) =>
    SellingService.getProductsToDeliver(getCurrentUserId(), from, to, withFinished).then((response) => {
      if (response.data && response.data.success) {
        return response.data.success;
      }
      throw new Error((response.data && response.data.error) || "Erreur de chargement");
    });

  const load = (from = dateFrom, to = dateTo, withFinished = includeFinished) => {
    if (!from || !to) {
      showError("Sélectionnez une date de début et une date de fin");
      return;
    }
    if (from > to) {
      showError("La date de début doit être antérieure à la date de fin");
      return;
    }
    startLoading();
    Promise.all([
      fetchRange(from, to, withFinished),
      fetchRange(LATE_FROM, addDays(today, -1), withFinished),
      fetchRange(today, addDays(today, 30), withFinished),
    ])
      .then(([main, overdue, next30]) => {
        setPeriod({ ...main, dateFrom: from, dateTo: to });
        setLate({ bdcs: overdue.bdcs || [], details: overdue.details || [] });
        setHorizon({ bdcs: next30.bdcs || [], details: next30.details || [] });
        setSelected("all");
        setProducts(null);
        setPage(0);
      })
      .catch((error) => {
        setPeriod(null);
        setLate(emptyLoad);
        setHorizon(emptyLoad);
        showError(error.message || "Erreur : il y a un problème de connexion");
      })
      .finally(() => stopLoading());
  };

  useEffect(() => {
    load();
  }, []);

  const onReset = () => {
    const r = defaultRange();
    setDateFrom(r.from);
    setDateTo(r.to);
    setIncludeFinished(false);
    load(r.from, r.to, false);
  };

  // per-day figures, computed from the same data the list and the export use
  const cards = useMemo(() => {
    if (!period) return [];
    const byDay = {};
    const bump = (key, bdcs, qty) => {
      byDay[key] = byDay[key] || { bdcs: 0, qty: 0 };
      byDay[key].bdcs += bdcs;
      byDay[key].qty += qty;
    };
    (period.bdcs || []).forEach((b) => bump(dayKey(b.deliveryDate), 1, 0));
    (period.details || []).forEach((d) => bump(dayKey(d.deliveryDate), 0, Number(d.quantity) || 0));

    const keys = [];
    for (let k = period.dateFrom; k <= period.dateTo && keys.length < 400; k = addDays(k, 1)) {
      keys.push(k);
    }
    const shown = keys.length <= MAX_DAY_CARDS ? keys : keys.filter((k) => byDay[k] && byDay[k].bdcs > 0);
    return shown.map((k) => ({ key: k, bdcs: (byDay[k] || {}).bdcs || 0, qty: (byDay[k] || {}).qty || 0 }));
  }, [period]);

  const totals = useMemo(() => {
    const sum = (list) => ({
      bdcs: list.bdcs.length,
      qty: list.details.reduce((s, d) => s + (Number(d.quantity) || 0), 0),
    });
    return {
      all: period ? sum({ bdcs: period.bdcs || [], details: period.details || [] }) : { bdcs: 0, qty: 0 },
      late: sum(late),
    };
  }, [period, late]);

  // tomorrow, and "within N days" (from today): number of orders and of products, whatever the chosen period
  const windows = useMemo(() => {
    const defs = [
      { key: "tomorrow", label: "Demain", from: addDays(today, 1), to: addDays(today, 1) },
      { key: "7", label: "Dans 7 jours", from: today, to: addDays(today, 7) },
      { key: "14", label: "Dans 14 jours", from: today, to: addDays(today, 14) },
      { key: "30", label: "Dans 30 jours", from: today, to: addDays(today, 30) },
    ];
    return defs.map((w) => {
      const inside = (value) => {
        const k = dayKey(value);
        return k >= w.from && k <= w.to;
      };
      return {
        ...w,
        bdcs: horizon.bdcs.filter((b) => inside(b.deliveryDate)).length,
        qty: horizon.details
          .filter((d) => inside(d.deliveryDate))
          .reduce((sum, d) => sum + (Number(d.quantity) || 0), 0),
      };
    });
  }, [horizon, today]);

  const applyWindow = (w) => {
    setDateFrom(w.from);
    setDateTo(w.to);
    load(w.from, w.to, includeFinished);
  };

  const selectedBdcs = useMemo(() => {
    if (!period) return [];
    if (selected === "all") return period.bdcs || [];
    if (selected === "late") return late.bdcs;
    return (period.bdcs || []).filter((b) => dayKey(b.deliveryDate) === selected);
  }, [period, late, selected]);

  const selectedLabel = () => {
    if (selected === "all") return `Toute la période (${formatPeriodDate(period.dateFrom)} - ${formatPeriodDate(period.dateTo)})`;
    if (selected === "late") return "En retard";
    return dayLabel(selected);
  };

  const onSelect = (key) => {
    setSelected(key);
    setProducts(null);
    setPage(0);
  };

  const onConsultProducts = () => {
    let from = period.dateFrom;
    let to = period.dateTo;
    let label = null;
    if (selected === "late") {
      from = LATE_FROM;
      to = addDays(today, -1);
      label = `En retard (avant le ${formatPeriodDate(today)})`;
    } else if (selected !== "all") {
      from = selected;
      to = selected;
    }
    startLoading();
    fetchRange(from, to, includeFinished)
      .then((result) => setProducts({ ...result, label: label || undefined }))
      .catch((error) => showError(error.message || "Erreur : il y a un problème de connexion"))
      .finally(() => stopLoading());
  };

  const onConsult = (bdc) => {
    sessionStorage.setItem("refDocument", bdc.reference);
    sessionStorage.setItem("typeDocument", "bdc");
    window.open("/getDocumentSalesByRef");
  };

  const pageCount = Math.max(1, Math.ceil(selectedBdcs.length / PAGE_SIZE));
  const pageRows = selectedBdcs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const hasProducts = products && products.categories && products.categories.length > 0;

  const DayCard = ({ id, title, subtitle, data, tone }) => {
    const active = selected === id;
    const empty = data.bdcs === 0;
    const accent = tone === "late" ? "#dc3545" : tone === "today" ? "#007bff" : "#343a40";
    return (
      <button
        type="button"
        onClick={() => onSelect(id)}
        className="text-left bg-white"
        style={{
          ...cardStyle,
          border: active ? `2px solid ${accent}` : "1px solid #dee2e6",
          padding: active ? "9px 11px" : "10px 12px",
          opacity: empty && !active ? 0.6 : 1,
          minWidth: 0,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600, color: tone === "late" ? "#dc3545" : "#6c757d", textTransform: "capitalize" }}>
          {title}
        </div>
        {subtitle && <div style={{ fontSize: 11, color: "#6c757d" }}>{subtitle}</div>}
        <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, marginTop: 6 }}>{data.bdcs}</div>
        <div style={{ fontSize: 11, color: "#6c757d" }}>livraison{data.bdcs > 1 ? "s" : ""}</div>
        <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>{formatQty(data.qty) || 0}</div>
        <div style={{ fontSize: 11, color: "#6c757d" }}>produits</div>
      </button>
    );
  };

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <h4 className="mb-1">
            <i className="fas fa-truck mr-2"></i>Suivi des livraisons
          </h4>
          <small className="text-muted">
            Bons de commande à livrer (En attente ou En cours), par date de livraison
          </small>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-dark mt-3" style={cardStyle}>
            <div className="card-body pb-2">
              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <label htmlFor="slDateFrom" style={labelStyle}>
                      Livraison du
                    </label>
                    <input
                      id="slDateFrom"
                      type="date"
                      className="form-control"
                      value={dateFrom}
                      max={dateTo || undefined}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label htmlFor="slDateTo" style={labelStyle}>
                      Au
                    </label>
                    <input
                      id="slDateTo"
                      type="date"
                      className="form-control"
                      value={dateTo}
                      min={dateFrom || undefined}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <div className="form-check mb-3">
                    <input
                      id="slFinished"
                      type="checkbox"
                      className="form-check-input"
                      checked={includeFinished}
                      onChange={(e) => setIncludeFinished(e.target.checked)}
                    />
                    <label htmlFor="slFinished" className="form-check-label">
                      Inclure les BDC terminés et livrés
                    </label>
                  </div>
                </div>
                <div className="col-md-3 d-flex align-items-end justify-content-end">
                  <div className="mb-3">
                    <button className="btn btn-outline-dark mr-2" onClick={onReset}>
                      <i className="fas fa-undo mr-1"></i> Réinitialiser
                    </button>
                    <button className="btn btn-dark" onClick={() => load()}>
                      <i className="fas fa-search mr-1"></i> Appliquer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {period && (
            <>
              <div
                className="mb-3"
                style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}
              >
                {windows.map((w) => {
                  const active = period.dateFrom === w.from && period.dateTo === w.to;
                  return (
                    <button
                      key={w.key}
                      type="button"
                      onClick={() => applyWindow(w)}
                      className="text-left bg-white"
                      style={{
                        ...cardStyle,
                        border: active ? "2px solid #343a40" : "1px solid #dee2e6",
                        padding: active ? "11px 13px" : "12px 14px",
                      }}
                      title={
                        w.from === w.to
                          ? `Livraisons du ${formatPeriodDate(w.from)}`
                          : `Livraisons du ${formatPeriodDate(w.from)} au ${formatPeriodDate(w.to)}`
                      }
                    >
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{w.label}</div>
                      <div style={{ fontSize: 11, color: "#6c757d" }}>
                        {w.from === w.to
                          ? formatPeriodDate(w.from)
                          : `${formatPeriodDate(w.from)} - ${formatPeriodDate(w.to)}`}
                      </div>
                      <div className="d-flex justify-content-between mt-2">
                        <div>
                          <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{w.bdcs}</div>
                          <div style={{ fontSize: 11, color: "#6c757d" }}>bon{w.bdcs > 1 ? "s" : ""} de commande</div>
                        </div>
                        <div className="text-right">
                          <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{formatQty(w.qty) || 0}</div>
                          <div style={{ fontSize: 11, color: "#6c757d" }}>produits</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div
                className="mb-2"
                style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(118px, 1fr))", gap: 8 }}
              >
                <DayCard id="all" title="Toute la période" subtitle={`${cards.length} jour(s)`} data={totals.all} />
                {totals.late.bdcs > 0 && (
                  <DayCard
                    id="late"
                    title="En retard"
                    subtitle={`avant le ${formatPeriodDate(today)}`}
                    data={totals.late}
                    tone="late"
                  />
                )}
                {cards.map((c) => (
                  <DayCard
                    key={c.key}
                    id={c.key}
                    title={c.key === today ? "Aujourd'hui" : dayLabel(c.key)}
                    subtitle={c.key === today ? dayLabel(c.key) : null}
                    data={c}
                    tone={c.key === today ? "today" : undefined}
                  />
                ))}
              </div>
              <p className="text-muted mb-3" style={{ fontSize: 12 }}>
                Cliquez sur un jour pour filtrer la liste et les produits à livrer.
                {(new Date(period.dateTo) - new Date(period.dateFrom)) / 86400000 + 1 > MAX_DAY_CARDS
                  ? " Seuls les jours avec une livraison sont affichés."
                  : ""}
              </p>

              <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
                <strong>
                  {selectedLabel()} : {selectedBdcs.length} bon(s) de commande
                </strong>
                {products ? (
                  <button className="btn btn-outline-dark" onClick={() => setProducts(null)}>
                    <i className="fas fa-arrow-left mr-1"></i> Retour à la liste des BDC
                  </button>
                ) : (
                  <button className="btn btn-dark" disabled={selectedBdcs.length === 0} onClick={onConsultProducts}>
                    <i className="fas fa-boxes mr-1"></i> Consulter les produits à livrer
                  </button>
                )}
              </div>
            </>
          )}

          {period && !products && (
            <div className="card" style={cardStyle}>
              <div className="card-body table-responsive p-0">
                {selectedBdcs.length === 0 ? (
                  <div className="alert alert-info m-3">
                    Aucun bon de commande à livrer pour cette sélection.
                  </div>
                ) : (
                  <table className="table table-hover table-striped mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th>N° BDC</th>
                        <th>Client</th>
                        <th>Date du BDC</th>
                        <th>Date de livraison</th>
                        <th>Statut</th>
                        <th className="text-center">Consulter</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((b) => {
                        const st = STATE_LIVRAISON[b.stateLivraison] || STATE_LIVRAISON[0];
                        const isLate = dayKey(b.deliveryDate) < today;
                        return (
                          <tr key={b.id}>
                            <td>{b.reference}</td>
                            <td>{b.client}</td>
                            <td>{b.creationDate ? new Date(b.creationDate).toLocaleDateString("fr-FR") : ""}</td>
                            <td className={isLate ? "text-danger font-weight-bold" : ""}>{formatDate(b.deliveryDate)}</td>
                            <td>
                              <span className={`badge ${st.cls}`}>{st.label}</span>
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-outline-dark"
                                onClick={() => onConsult(b)}
                                title="Ouvrir le bon de commande"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              {pageCount > 1 && (
                <div className="card-footer d-flex justify-content-between align-items-center">
                  <button className="btn btn-sm btn-outline-dark" disabled={page === 0} onClick={() => setPage(page - 1)}>
                    Précédent
                  </button>
                  <span>
                    Page {page + 1} / {pageCount}
                  </span>
                  <button
                    className="btn btn-sm btn-outline-dark"
                    disabled={page >= pageCount - 1}
                    onClick={() => setPage(page + 1)}
                  >
                    Suivant
                  </button>
                </div>
              )}
            </div>
          )}

          {period && products && (
            <div className="card" style={cardStyle}>
              <div className="card-header d-flex flex-wrap justify-content-between align-items-center">
                <h3 className="card-title mb-0">{`Produits à livrer : ${periodLabel(products)}`}</h3>
                <div>
                  <button className="btn btn-outline-dark mr-2" disabled={!hasProducts} onClick={() => exportToExcel(products)}>
                    <i className="fas fa-file-excel mr-1"></i> Exporter en Excel
                  </button>
                  <button
                    className="btn btn-dark"
                    disabled={!hasProducts}
                    onClick={() => {
                      if (!printConsolidation(products)) {
                        showError("Impossible d'ouvrir la fenêtre d'impression (pop-up bloquée ?)");
                      }
                    }}
                  >
                    <i className="fas fa-print mr-1"></i> Imprimer
                  </button>
                </div>
              </div>
              <div className="card-body">
                {!hasProducts ? (
                  <div className="alert alert-info mb-0">Aucun produit à livrer pour cette sélection.</div>
                ) : (
                  <>
                    {products.categories.map((cat) => (
                      <div key={cat.idCategory === null ? "none" : cat.idCategory} className="mb-4">
                        <h5>Catégorie : {cat.category}</h5>
                        <div className="table-responsive">
                          <table className="table table-bordered table-sm mb-0">
                            <thead className="thead-light">
                              <tr>
                                <th style={{ width: "18%" }}>Référence</th>
                                <th>Libellé du produit</th>
                                <th style={{ width: "14%" }} className="text-right">
                                  Quantité totale
                                </th>
                                <th style={{ width: "32%" }}>Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cat.products.map((p) => (
                                <tr key={p.idProduct}>
                                  <td>{p.reference}</td>
                                  <td>{p.label}</td>
                                  <td className="text-right">{formatQty(p.quantity)}</td>
                                  <td>
                                    {(p.notes || []).map((n, i) => (
                                      <div key={i} style={{ whiteSpace: "pre-wrap" }}>
                                        {noteToText(n)}
                                      </div>
                                    ))}
                                  </td>
                                </tr>
                              ))}
                              <tr className="font-weight-bold bg-light">
                                <td></td>
                                <td>Total {cat.category}</td>
                                <td className="text-right">{formatQty(cat.totalQuantity)}</td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                    <div className="font-weight-bold text-right">Total général : {formatQty(products.totalQuantity)}</div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={(e, reason) => reason !== "clickaway" && setAlert((a) => ({ ...a, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert variant="filled" severity="error" onClose={() => setAlert((a) => ({ ...a, open: false }))}>
          {alert.text}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default withLoader(SuiviLivraisons);
